import React, {useEffect, useMemo, useRef, useState} from 'react'
import {
	Alert,
	AlertTitle,
	alpha, Badge,
	Box,
	Button,
	Checkbox, Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	Drawer,
	FormControl,
	FormControlLabel,
	IconButton,
	InputLabel, Link,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	Select,
	Switch, TextField,
	Tooltip,
	Typography,
	useTheme
} from '@mui/material'
import Grid                      from '@mui/material/Unstable_Grid2'
import {doDelete, doGet, doPost} from '../../util/do-fetch'
import {getSupabase}             from '../../util/supabase-utils'
import EditableLabel             from '../EditableLabel'
import LeftRight                                                                    from '../LeftRight'
import {
	Add,
	ChecklistOutlined,
	ChevronLeft,
	ChevronRight,
	Close, DeleteOutline,
	Folder,
	RemoveCircle, RemoveCircleOutline, RemoveCircleOutlined,
	Videocam
} from '@mui/icons-material'
import {isBlankOrEmpty}                                                             from '../../util/utils'
import {fetchTTSModels, fetchVoices}                             from "../../util/admin"
import CheckIcon from "@mui/icons-material/Check"
import useQuery from "../../hooks/useQuery"

const CUSTOM_CLIPS_DIR = '.custom-clips'
const CUSTOM_CLIPS_PATH = CUSTOM_CLIPS_DIR + '/'

const INDENT_PX = 2
const LEFT_COL_W = 44
const RIGHT_COL_W = 72

const makeRefKey = (type, id) => `${type}:${id}`

const buildClipsFromProducts = (videoProducts) => {
	const clips = []

	;(videoProducts || []).forEach(vp => {
		const productId = vp.productId
		const product = vp.product
		const attachedBefore = vp.attachedBefore || {}
		const attachedAfter = vp.attachedAfter || {}

		const selectedSections = vp.selections?.sections || {}
		const selectedTopics = vp.selections?.topics || {}
		const selectedQuestions = vp.selections?.questions || {}

		const sections = product?.topics || []
		sections.forEach(section => {
			if (selectedSections[section.id]) {
				const refKey = makeRefKey('section', section.id)
				const name = section.name || `Section ${section.id}`

				if (attachedBefore[refKey]) {
					clips.push({
						productId,
						refId: section.id,
						refType: 'topic',
						refName: attachedBefore[refKey],
						renderType: 'custom-clip',
						before: true
					})
				}

				clips.push({
					productId,
					refId: section.id,
					refType: 'topic',
					refName: name,
					renderType: 'section'
				})

				if (attachedAfter[refKey]) {
					clips.push({
						productId,
						refId: section.id,
						refType: 'topic',
						refName: attachedAfter[refKey],
						renderType: 'custom-clip',
						after: true
					})
				}
			}

			const topics = section?.topics || []
			topics.forEach(topic => {
				if (selectedTopics[topic.id]) {
					const refKey = makeRefKey('topic', topic.id)
					const name = topic.name || `Topic ${topic.id}`

					if (attachedBefore[refKey]) {
						clips.push({
							productId,
							refId: topic.id,
							refType: 'topic',
							refName: attachedBefore[refKey],
							renderType: 'custom-clip',
							before: true
						})
					}

					clips.push({
						productId,
						refId: topic.id,
						refType: 'topic',
						refName: name,
						renderType: 'title'
					})

					if (attachedAfter[refKey]) {
						clips.push({
							productId,
							refId: topic.id,
							refType: 'topic',
							refName: attachedAfter[refKey],
							renderType: 'custom-clip',
							after: true
						})
					}
				}

				const questions = topic?.questions || []
				questions.forEach(q => {
					if (selectedQuestions[q.id]) {
						const refKey = makeRefKey('question', q.id)
						const name = q.question || `Question ${q.id}`

						if (attachedBefore[refKey]) {
							clips.push({
								productId,
								refId: q.id,
								refType: 'question',
								refName: attachedBefore[refKey],
								renderType: 'custom-clip',
								before: true
							})
						}

						clips.push({
							productId,
							refId: q.id,
							refType: 'question',
							refName: name,
							renderType: 'question'
						})

						if (attachedAfter[refKey]) {
							clips.push({
								productId,
								refId: q.id,
								refType: 'question',
								refName: attachedAfter[refKey],
								renderType: 'custom-clip',
								after: true
							})
						}
					}
				})
			})
		})
	})

	return clips
}

const buildRows = (videoProduct) => {
	const product = videoProduct.product
	const selections = videoProduct.selections || {sections: {}, topics: {}, questions: {}}
	const attachedBefore = videoProduct.attachedBefore || {}
	const attachedAfter = videoProduct.attachedAfter || {}

	const rows = []

	const pushBefore = (refKey, indent) => {
		const key = attachedBefore[refKey]
		if (!key) return
		rows.push({
			key: `${refKey}:before`,
			kind: 'before',
			refKey,
			indent,
			label: `${key.split('/')
				.pop()}`,
			removalPlacement: 'before'
		})
	}

	const pushAfter = (refKey, indent) => {
		const key = attachedAfter[refKey]
		if (!key) return
		rows.push({
			key: `${refKey}:after`,
			kind: 'after',
			refKey,
			indent,
			label: `${key.split('/')
				.pop()}`,
			removalPlacement: 'after'
		})
	}

	const sections = product?.topics || []
	sections.forEach(section => {
		const sectionRefKey = makeRefKey('section', section.id)
		const sectionSelected = !!selections.sections?.[section.id]

		if (sectionSelected) pushBefore(sectionRefKey, 0)

		rows.push({
			key: sectionRefKey,
			kind: 'section',
			refType: 'section',
			refId: section.id,
			refKey: sectionRefKey,
			indent: 0,
			label: section.title || section.name || `Section ${section.id}`,
			selected: sectionSelected
		})

		if (sectionSelected) pushAfter(sectionRefKey, 0)

		const topics = section?.topics || []
		topics.forEach(topic => {
			const topicRefKey = makeRefKey('topic', topic.id)
			const topicSelected = !!selections.topics?.[topic.id]

			if (topicSelected) pushBefore(topicRefKey, 1)

			rows.push({
				key: topicRefKey,
				kind: 'topic',
				refType: 'topic',
				refId: topic.id,
				refKey: topicRefKey,
				indent: 1,
				label: topic.title || topic.name || `Topic ${topic.id}`,
				selected: topicSelected
			})

			if (topicSelected) pushAfter(topicRefKey, 1)

			const questions = topic?.questions || []
			questions.forEach(q => {
				const qRefKey = makeRefKey('question', q.id)
				const questionSelected = !!selections.questions?.[q.id]

				if (questionSelected) pushBefore(qRefKey, 2)

				rows.push({
					key: qRefKey,
					kind: 'question',
					refType: 'question',
					refId: q.id,
					refKey: qRefKey,
					indent: 2,
					label: q.question || q.title || `Question ${q.id}`,
					selected: questionSelected
				})

				if (questionSelected) pushAfter(qRefKey, 2)
			})
		})
	})

	return rows
}


const RowItem = ({
	                 row,
	                 theme,
	                 onToggle,
	                 onOpenMenu,
	                 onOpenSelectMenu,
	                 onRemoveClip
                 }) => {
	const selectable = row.kind === 'section' || row.kind === 'topic' || row.kind === 'question'
	const removable = row.kind === 'before' || row.kind === 'after'
	const bold = row.kind === 'section' || row.kind === 'topic'
	const before = row.kind === 'before'
	const after = row.kind === 'after'
	const showPlus = selectable && row.selected

	return (
		<ListItem
			disableGutters
			sx={{
				px: 1,
				mt: removable ? -1 : 1,
				mb: removable ? -1 : 1,
				pt: after ? 0 : 1,
				pb: before ? 0 : 1,
				border: `1px solid ${removable ? 'transparent' : row.selected ? theme.palette.divider : 'transparent'}`,
				borderRadius: 1,
				backgroundColor: removable ? 'transparent' : row.selected ? 'background.paper' : 'transparent',
				cursor: 'pointer'
			}}
			onClick={(e) => {
				e.stopPropagation()
				onToggle(row, !row.selected)
			}}
		>
			<Box sx={{width: LEFT_COL_W, display: 'flex', justifyContent: 'center'}}>
				{selectable && (
					<Checkbox checked={!!row.selected}/>
				)}
			</Box>

			<Box sx={{flex: 1, pl: removable ? 0 : row.indent * INDENT_PX}}>
				<Typography
					variant="body2"
					sx={{
						fontWeight: bold ? 'bold' : 'normal',
						border: removable ? `1px solid ${theme.palette.divider}` : 'none',
						borderTopWidth: before ? 1 : 0,
						borderBottomWidth: after ? 1 : 0,
						backgroundColor: removable ? alpha(theme.palette.background.paper, 0.7) : 'transparent',
						display: 'inline-block',
						pl: removable ? 1.5 : 0,
						py: removable ? 1 : 'auto',
						fontStyle: removable ? 'italic' : 'normal',
						color: removable ? 'text.secondary' : row.selected ? 'text.primary' : 'text.disabled',
						minWidth: removable ? '500px' : 'none'
					}}
				>
					{row.label}
					{
						//JSON.stringify(row, null, 2)
					}
				</Typography>
				{removable && (
					<IconButton
						size="small"
						sx={{mx: 0.5}}
						onClick={(e) => {
							e.stopPropagation()
							onRemoveClip(row)
						}}
					>
						<Close fontSize="16px" color="error"/>
					</IconButton>
				)}
			</Box>

			<Box sx={{width: RIGHT_COL_W, ml: 2, display: 'flex', justifyContent: 'flex-end', alignItems: 'center'}}>
				{
					<IconButton
						sx={{visibility: showPlus ? "visible" : "hidden"}}
						size="medium"
						onClick={(e) => onOpenMenu(e, row)}
					>
						<Add fontSize="small"/>
					</IconButton>
				}
				{
					<IconButton
						sx={{visibility: onOpenSelectMenu ? "visible" : "hidden"}}
						size="medium"
						onClick={(e) => onOpenSelectMenu(e, row)}
					>
						<ChecklistOutlined fontSize="small" />
					</IconButton>
				}
			</Box>
		</ListItem>
	)
}

const ProductContentSelector = ({videoProduct, hideExcluded, onChange, onGetCustomClip, onRemove}) => {
	const theme = useTheme()
	const product = videoProduct.product
	const selections = videoProduct.selections || {sections: {}, topics: {}, questions: {}}

	const setSelections = (nextSelections) => {
		onChange({
			...videoProduct,
			selections: nextSelections
		})
	}

	const setAttached = (placement, refKey, clipKey) => {
		const next = {...videoProduct}
		if (placement === 'before') next.attachedBefore = {...(next.attachedBefore || {}), [refKey]: clipKey}
		if (placement === 'after') next.attachedAfter = {...(next.attachedAfter || {}), [refKey]: clipKey}
		onChange(next)
	}

	const clearAttached = (placement, refKey) => {
		const next = {...videoProduct}
		if (placement === 'before') {
			const m = {...(next.attachedBefore || {})}
			delete m[refKey]
			next.attachedBefore = m
		}
		if (placement === 'after') {
			const m = {...(next.attachedAfter || {})}
			delete m[refKey]
			next.attachedAfter = m
		}
		onChange(next)
	}

	const toggleRow = (row, isSelected) => {
		const next = {...selections}
		if (row.refType === 'section') {
			const updated = {...(next.sections || {})}
			if (isSelected) updated[row.refId] = true
			else delete updated[row.refId]
			next.sections = updated
		}
		if (row.refType === 'topic') {
			const updated = {...(next.topics || {})}
			if (isSelected) updated[row.refId] = true
			else delete updated[row.refId]
			next.topics = updated
		}
		if (row.refType === 'question') {
			const updated = {...(next.questions || {})}
			if (isSelected) updated[row.refId] = true
			else delete updated[row.refId]
			next.questions = updated
		}
		setSelections(next)
	}

	const rows = useMemo(() => {
		return buildRows(videoProduct)
	}, [videoProduct])

	const selectedRows = useMemo(() => {
		if (!rows) return rows
		return hideExcluded
			? rows.filter(row => row.selected || row.kind === 'before' || row.kind === 'after')
			: rows
	}, [rows, hideExcluded])

	const selectedCount = useMemo(() => {
		const sel = rows?.filter(row => row.selected || row.kind === 'before' || row.kind === 'after') || []
		return sel.length
	}, [rows])

	const selectedSections = useMemo(() => {
		const sel = rows?.filter(row => row.selected && row.kind === 'section') || []
		return sel.length
	}, [rows])

	const selectedTopics = useMemo(() => {
		const sel = rows?.filter(row => row.selected && row.kind === 'topic') || []
		return sel.length
	}, [rows])

	const selectedQuestions = useMemo(() => {
		const sel = rows?.filter(row => row.selected && row.kind === 'question') || []
		return sel.length
	}, [rows])

	const selectedClips = useMemo(() => {
		const sel = rows?.filter(row => row.kind === 'before' || row.kind === 'after') || []
		return sel.length
	}, [videoProduct.attachedBefore, videoProduct.attachedAfter])

	const [menuAnchorEl, setMenuAnchorEl] = useState(null)
	const [selectMenuAnchorEl, setSelectMenuAnchorEl] = useState(null)
	const [menuRow, setMenuRow] = useState(null)
	const menuOpen = Boolean(menuAnchorEl)
	const selectMenuOpen = Boolean(selectMenuAnchorEl)

	const openMenu = (e, row) => {
		e.stopPropagation()
		setMenuAnchorEl(e.currentTarget)
		setMenuRow(row)
	}

	const closeMenu = () => {
		setMenuAnchorEl(null)
		setMenuRow(null)
	}

	const handleRemoveClipRow = (row) => {
		if (row.removalPlacement === 'before') clearAttached('before', row.refKey)
		if (row.removalPlacement === 'after') clearAttached('after', row.refKey)
	}

	const openSelectMenu = (e, row) => {
		e.stopPropagation()
		setSelectMenuAnchorEl(e.currentTarget)
		setMenuRow(row)
	}

	const closeSelectMenu = () => {
		setSelectMenuAnchorEl(null)
		setMenuRow(null)
	}

	const selectAll = (row) => {
		toggleWithChildren(rows, row, null)
	}

	const selectNone = (row) => {
		if(!row)  {
			setSelections({
				sections: {},
				topics: {},
				questions: {}
			})
		}
		else {
			toggleWithChildren(rows, row, 0)
		}
	}

	const toggleWithChildren = (rows, row = null, limit = null) => {
		const next = {
			sections: {...(selections.sections || {})},
			topics: {...(selections.topics || {})},
			questions: {...(selections.questions || {})}
		}

		const startIndex = row
			? rows.findIndex(r => r.key === row.key)
			: -1

		if (row && startIndex === -1) return

		const baseIndent = row ? row.indent : -1

		// collect scoped rows
		const scoped = []
		for (let i = startIndex + 1; i < rows.length; i++) {
			const r = rows[i]
			if (r.indent <= baseIndent) break
			scoped.push(r)
		}

		// collect questions grouped by topic
		const groups = []
		let currentGroup = null

		for (const r of scoped) {
			if (r.kind === 'topic') {
				currentGroup = []
				groups.push(currentGroup)
			}
			else if (r.kind === 'question') {
				if (!currentGroup) {
					currentGroup = []
					groups.push(currentGroup)
				}
				currentGroup.push(r)
			}
		}

		// clear existing selections in scope
		for (const group of groups) {
			for (const q of group) {
				delete next.questions[q.refId]
			}
		}

		// apply selection
		if (limit === null) {
			// Select All
			for (const group of groups) {
				for (const q of group) {
					next.questions[q.refId] = true
				}
			}
		}
		else if (limit > 0) {
			for (const group of groups) {
				group.slice(0, limit).forEach(q => {
					next.questions[q.refId] = true
				})
			}
		}
		// limit === 0 → Select None (already cleared)

		// ensure parents ONLY when action started at section or root
		if (!row || row.kind === 'section') {
			const selectedQuestionIds = new Set(
				Object.keys(next.questions).map(Number)
			)

			for (let i = 0; i < rows.length; i++) {
				const r = rows[i]
				if (r.kind !== 'question' || !selectedQuestionIds.has(r.refId)) continue

				// section parent
				for (let j = i - 1; j >= 0; j--) {
					const parent = rows[j]
					if (parent.kind === 'section') {
						next.sections[parent.refId] = true
						break
					}
				}

				// topic parent (ONLY when row is null or section)
				for (let j = i - 1; j >= 0; j--) {
					const parent = rows[j]
					if (parent.kind === 'topic') {
						next.topics[parent.refId] = true
						break
					}
				}
			}
		}

		// if action started on a topic, explicitly select that topic
		if (row?.kind === 'topic' && limit !== 0) {
			next.topics[row.refId] = true
		}

		// explicit deselection when Select None is used
		if (row && limit === 0) {
			if (row.kind === 'section') {
				// deselect section
				delete next.sections[row.refId]

				// deselect child topics
				for (const r of scoped) {
					if (r.kind === 'topic') {
						delete next.topics[r.refId]
					}
				}
			}

			if (row.kind === 'topic') {
				// deselect topic itself
				delete next.topics[row.refId]
			}
		}

		setSelections(next)
	}

	return (
		<Box sx={{
			border: '1px solid ' + theme.palette.divider,
			backgroundColor: 'background.default',
			borderRadius: 2,
			p: 2,
			mb: 2
		}}>
			<Box display="flex" justifyContent="space-between" alignItems="center" sx={{mb: 1}}>
				<Box>
					<div>
						<Badge badgeContent={selectedCount} color="error">
							<Typography sx={{fontSize: 18, fontWeight: 600}}>
								{product?.name}
							</Typography>
						</Badge>
					</div>
					<div>
						{/*
						<Chip size="small" sx={{mr: 0.5}} variant={"outlined"} label={`${selectedSections} sections`}/>
						<Chip size="small" sx={{mr: 0.5}} variant={"outlined"} label={`${selectedTopics} topics`}/>
						<Chip size="small" sx={{mr: 0.5}} variant={"outlined"}
						      label={`${selectedQuestions} questions`}/>
						<Chip size="small" sx={{mr: 0.5}} variant={"outlined"} label={`${selectedClips} clips`}/>
*/}
						<div style={{fontSize: 12}}>
							{
								[
									{ name: "Section", count: selectedSections },
									{ name: "Topic", count: selectedTopics },
									{ name: "Question", count: selectedQuestions },
									{ name: "Clip", count: selectedClips }
								].map((o,i) => (
									<span key={`count-${o.name}`} style={{marginRight: "5px"}}>
										{i > 0 ? '| ' : ''} {o.count} {o.count === 1 ? o.name : `${o.name}s`}
									</span>
								))
							}
						</div>

					</div>
					{/*<div style={{fontSize: 12, opacity: 0.7}}>{selectedCount} selected</div>*/}
				</Box>
				<Box>
					<IconButton onClick={(e) => {
						e.stopPropagation()
						setMenuRow(null)
						openSelectMenu(e, null)
					}}>
						<ChecklistOutlined color={"primary"}/>
					</IconButton>
					<IconButton sx={{ml: 1}} color="error" onClick={onRemove}>
						<DeleteOutline/>
					</IconButton>
				</Box>
			</Box>

			<List dense sx={{p: 0}}>
				{selectedRows.map(r => (
					<RowItem
						key={r.key}
						row={r}
						theme={theme}
						onToggle={toggleRow}
						onOpenMenu={openMenu}
						onOpenSelectMenu={r.kind !== "question" && openSelectMenu}
						onRemoveClip={handleRemoveClipRow}
					/>
				))}
			</List>

			<Menu
				anchorEl={menuAnchorEl}
				open={!!menuOpen}
				onClose={closeMenu}
			>
				<MenuItem
					onClick={async () => {
						const mr = menuRow
						closeMenu()
						const clip = await onGetCustomClip()
						if (clip?.key && mr?.refKey) setAttached('before', mr.refKey, clip.key)
					}}
				>
					Add Clip Before
				</MenuItem>
				<MenuItem
					onClick={async () => {
						const mr = menuRow
						closeMenu()
						const clip = await onGetCustomClip()
						if (clip?.key && mr?.refKey) setAttached('after', mr.refKey, clip.key)
					}}
				>
					Add Clip After
				</MenuItem>
			</Menu>

			<Menu
				anchorEl={selectMenuAnchorEl}
				open={!!selectMenuOpen}
				onClose={closeSelectMenu}
			>
				<MenuItem
					onClick={(e) => {
						e.stopPropagation()
						selectAll(menuRow)
						closeSelectMenu()
					}}
				>
					Select All
				</MenuItem>
				<MenuItem
					onClick={async (e) => {
						e.stopPropagation()
						selectNone(menuRow)
						closeSelectMenu()
					}}
				>
					Select None
				</MenuItem>
				<Divider/>
				{
					Array.from({length: 16}, (_, index) => index)
						.map(i => <MenuItem key={`select-some-${i}`} onClick={() => {
							toggleWithChildren(rows, menuRow, i)
							closeSelectMenu()
						}}>
							Select First {i}
						</MenuItem>)
				}
			</Menu>

			{/*
			<Box display={"fixed"} overflow={"scroll"} position={"fixed"} top={0} left={0} bottom={0} width={"500px"} border={"2px solid red"} backgroundColor={"#efefef"} zIndex={2000}>
				<List dense sx={{p: 0}}>
					{selectedRows.map(r => (
						<ListItem sx={{maxWidth: "400px"}}>
							{JSON.stringify(r)}
						</ListItem>
					))}
				</List>
			</Box>
			*/}

		</Box>
	)
}

const VideoJobDialog = ({open, onCancel, onSaved}) => {
	const theme = useTheme()
	const {pid} = useQuery()
	const [saving, setSaving] = useState(false)
	const [showScript, setShowScript] = useState(false)
	const [showProductsDrawer, setShowProductsDrawer] = useState(false)
	const [showCustomClipsDrawer, setShowCustomClipsDrawer] = useState(false)
	const [resolveSelectClip, setResolveSelectClip] = useState(null)

	const [customClipsPath, setCustomClipsPath] = useState([CUSTOM_CLIPS_PATH])
	const [products, setProducts] = useState(null)
	const [customClips, setCustomClips] = useState(null)
	const [filteredClips, setFilteredClips] = useState(null)

	const [category, setCategory] = useState('All')
	const [keyword, setKeyword] = useState('')

	const [title, setTitle] = useState(null)
	const [errors, setErrors] = useState(null)

	const [videoProducts, setVideoProducts] = useState([])
	const [aspectRatio, setAspectRatio] = useState('16:9')
	const [aspectRatios] = useState(['16:9', '9:16'])
	const [ttsModels, setTtsModels] = useState([])
	const [voices, setVoices] = useState([])
	const [ttsModel, setTtsModel] = useState('gpt-4o-mini-tts')
	const [voiceId, setVoiceId] = useState('echo')
	const [jobCategories, setJobCategories] = useState([])
	const [videoCategoryId, setVideoCategoryId] = useState('')
	const [hideExcluded, setHideExcluded] = useState(false)
	const hasAutoAddedRef = useRef(false)

	useEffect(() => {
		if (!pid) return
		if (!products) return
		if (hasAutoAddedRef.current) return

		const product = products.find(p => String(p.id) === String(pid))
		if (!product) return

		hasAutoAddedRef.current = true
		addProduct(product)
	}, [pid, products])

	const filteredProducts = useMemo(() => {
		return products?.filter(p => {
			const matchCategory = category === 'All' || p.category?.id == category // used == on purpose
			const matchKeyword = keyword === '' || p.name.toLowerCase()
				.includes(keyword.toLowerCase())
			return matchCategory && matchKeyword
		}) || []
	}, [products, category, keyword])

	const categories = useMemo(() => {
		return products
			?.map(p => p.category)
			.filter(Boolean)
			.filter((c, i, arr) => arr.findIndex(x => x.id === c.id) === i) || []
	}, [products])

	const videoScript = useMemo(() => {
		return {
			title,
			aspectRatio,
			ttsModel,
			voiceId,
			categoryId: videoCategoryId || null,
			clips: buildClipsFromProducts(videoProducts)
		}
	}, [title, aspectRatio, ttsModel, voiceId, videoCategoryId, videoProducts])

	useEffect(() => {
		fetchProducts()
		fetchCustomClips()
		fetchModels()
		const fetchJobCategories = async () => {
			const { data } = await getSupabase()
				.from('category')
				.select('id, name, color, color2')
				.order('name', { ascending: true })
			if (data) setJobCategories(data)
		}
		fetchJobCategories()
	}, [])

	useEffect(() => {
		if (!customClips) {
			setFilteredClips(null)
			return
		}

		const currentPath = customClipsPath[customClipsPath.length - 1]
		setFilteredClips(customClips.filter(c => {
			const parts = c.key.split('/')
				.filter(Boolean)
			parts.pop()
			const path = parts.join('/') + '/'
			return path === currentPath && c.key !== currentPath
		}))
	}, [customClips, customClipsPath])

	const fetchProducts = async () => {
		try {
			const response = await doGet('/admin/products')
			setProducts(await response.json())
		}
		catch (err) {
			console.error(err)
		}
	}

	const fetchCustomClips = async () => {
		try {
			const response = await doGet('/admin/videos/custom-clips')
			setCustomClips(await response.json())
		}
		catch (err) {
			console.error(err)
		}
	}

	const fetchModels = async () => {
		const tts = await fetchTTSModels()
		const voices = await fetchVoices()
		setTtsModels(tts)
		setVoices(voices)
	}

	const productAlreadyAdded = (productId) => {
		return (videoProducts || []).some(vp => vp.productId === productId)
	}

	const addProduct = async (product) => {
		if (!product?.id) return
		if (productAlreadyAdded(product.id)) return

		try {
			const resp = await doGet(`/admin/products/${product.id}`)
			const fullProduct = await resp.json()

			setVideoProducts(prev => ([
				...(prev || []),
				{
					productId: fullProduct.id,
					product: fullProduct,
					selections: {sections: {}, topics: {}, questions: {}},
					attachedBefore: {},
					attachedAfter: {}
				}
			]))

			setShowProductsDrawer(false)
		}
		catch (err) {
			console.error(err)
		}
	}

	const validate = () => {
		const errs = []
		if (isBlankOrEmpty(title)) errs.push('Title is required.')
		if (!videoCategoryId) errs.push('Category is required.')
		if (isBlankOrEmpty(aspectRatio)) errs.push('Aspect Ratio is required.')
		if (isBlankOrEmpty(ttsModel)) errs.push('TTS Model is required.')
		if (isBlankOrEmpty(voiceId)) errs.push('Voice is required.')
		if (!videoProducts || videoProducts.length === 0) errs.push('At least one Product is required.')

		;
		(videoProducts || []).forEach(vp => {
			const a = Object.keys(vp.selections?.sections || {}).length
			const b = Object.keys(vp.selections?.topics || {}).length
			const c = Object.keys(vp.selections?.questions || {}).length
			if (a + b + c === 0) errs.push(`Product "${vp.product?.name}" must have at least one item selected.`)
		})

		return errs
	}

	const handleSave = async () => {
		setSaving(true)
		setErrors(null)
		try {
			const errs = validate()
			if (errs.length > 0) {
				setErrors(errs)
				return
			}

			// todo: remove pretty formatting
			const body = JSON.stringify(videoScript, null, 2)

			const options = { body }
			const response = await doPost('/admin/videos/jobs/create', options)
			const updated = await response.json()
			onSaved(updated)
		}
		catch (err) {
			console.error(err)
		}
		finally {
			setSaving(false)
		}
	}

	const handleVideoProductChange = (next) => {
		setVideoProducts(prev => {
			const updated = [...(prev || [])]
			const index = updated.findIndex(x => x.productId === next.productId)
			if (index === -1) return updated
			updated[index] = next
			return updated
		})
	}

	const handleRemoveVideoProduct = (productId) => {
		setVideoProducts(prev => (prev || []).filter(vp => vp.productId !== productId))
	}

	const handleGetCustomClip = async () => {
		return new Promise((resolve) => {
			setShowCustomClipsDrawer(true)
			setResolveSelectClip(() => resolve)
		})
	}

	const handleCustomClipSelection = (clip) => {
		if (!resolveSelectClip) return
		resolveSelectClip(clip)
		setShowCustomClipsDrawer(false)
		setResolveSelectClip(null)
	}

	const drawerOpen = showProductsDrawer || showCustomClipsDrawer
	let drawerTitle = ''
	let drawerBody = null

	if (showProductsDrawer) {
		drawerTitle = 'Products'
		drawerBody = (
			<Box>
				{!products && (
					<p style={{textAlign: 'center'}}>
						<CircularProgress size={14}/> Loading Products
					</p>
				)}
				{products && (
					<List>
						{filteredProducts.map(p => {
							const disabled = productAlreadyAdded(p.id)
							return (
								<ListItem
									key={`p-${p.id}`}
									onClick={() => (disabled ? null : addProduct(p))}
									sx={{
										cursor: disabled ? 'default' : 'pointer',
										justifyContent: 'space-between',
										py: 2,
										px: 3,
										my: 1,
										opacity: disabled ? 0.4 : 1,
										border: `1px solid ${theme.palette.divider}`
									}}
								>
									{p.name}
									<ChevronRight/>
								</ListItem>
							)
						})}
					</List>
				)}
			</Box>
		)
	}

	if (showCustomClipsDrawer) {
		drawerTitle = 'Custom Clips'
		drawerBody = (
			<Box>
				<Box>
					{customClipsPath && customClipsPath.length > 1 && (
						<Button onClick={() => setCustomClipsPath(prev => [...prev].slice(0, -1))}>
							<ChevronLeft/> Back
						</Button>
					)}
				</Box>

				{!filteredClips && (
					<p style={{textAlign: 'center'}}>
						<CircularProgress size={14}/> Loading Clips
					</p>
				)}

				{filteredClips && (
					<List>
						{filteredClips.map(c => {
							return c.key.endsWith('/')
								? (
									<ListItem key={`clip-${c.key}`} sx={{opacity: 0.8}}>
										<ListItemButton onClick={() => setCustomClipsPath(prev => [...prev, c.key])}>
											<ListItemIcon>
												<Folder/>
											</ListItemIcon>
											<ListItemText primary={c.key.split('/')
												.filter(Boolean)
												.pop()}/>
										</ListItemButton>
									</ListItem>
								)
								: (
									<ListItem key={`clip-${c.key}`} sx={{opacity: 0.9}}>
										<ListItemButton onClick={() => handleCustomClipSelection(c)}>
											<ListItemIcon>
												<Videocam/>
											</ListItemIcon>
											<ListItemText primary={c.key.split('/')
												.pop()}/>
										</ListItemButton>
									</ListItem>
								)
						})}
					</List>
				)}
			</Box>
		)
	}

	return (
		<Dialog open={!!open} fullScreen>
			<DialogTitle>
				<Box display="flex" justifyContent="space-between" alignItems="center">
					<div>Create Video Job</div>
					<IconButton onClick={onCancel}>
						<Close color="error"/>
					</IconButton>
				</Box>
			</DialogTitle>

			<Divider/>

			<DialogContent>
				<Grid container maxWidth="lg" sx={{margin: '0 auto'}}>
					<Grid xs={12}>
						<LeftRight
							left={
								<Box width={"612px"}>
									<TextField
										size="small"
										value={title}
										label={"Video Title"}
										autoFocus
										onChange={(e) => setTitle(e.target.value)}
										fullWidth={true}
									/>
								</Box>
							}

							right={
								<Box>
									<Button variant={"outlined"} onClick={() => setShowScript(true)} sx={{mr: 1}}>
										View Script
									</Button>
									<Button
										onClick={() => setShowProductsDrawer(true)}
										variant={"outlined"}
										color="primary"
										sx={{mr: 1}}
									>
										<Add/> Product
									</Button>
									<Button variant="contained" disabled={saving} onClick={handleSave}>
										Save Video
										{saving && <CircularProgress size={18}/>}
									</Button>
								</Box>
							}
						/>
					</Grid>

					{errors && (
						<Grid xs={12}>
							<Alert severity="error" onClose={() => setErrors(null)}>
								<AlertTitle>Error Saving Video Job</AlertTitle>
								{errors.map((e, eix) => <div key={`err-${eix}`}>{e}</div>)}
							</Alert>
						</Grid>
					)}

					<Grid xs={12}>
						<Box my={2}>
							<LeftRight
								left={
									<Box display="flex" gap={2}>
										<FormControl sx={{minWidth: 160}} size="small">
											<InputLabel>Format</InputLabel>
											<Select
												label="Format"
												value={aspectRatio}
												onChange={(e) => setAspectRatio(e.target.value)}
											>
												{aspectRatios.map(r => (
													<MenuItem key={r} value={r}>{r}</MenuItem>
												))}
											</Select>
										</FormControl>

										<FormControl sx={{minWidth: 220}} size="small">
											<InputLabel>TTS Model</InputLabel>
											<Select
												label="TTS Model"
												value={ttsModel}
												onChange={(e) => setTtsModel(e.target.value)}
											>
												{ttsModels.map(m => (
													<MenuItem key={m.name} value={m.name}>
														{m.name}
													</MenuItem>
												))}
											</Select>
										</FormControl>

										<FormControl sx={{minWidth: 200}} size="small">
											<InputLabel>Voice</InputLabel>
											<Select
												label="Voice"
												value={voiceId}
												onChange={(e) => setVoiceId(e.target.value)}
											>
												{voices.map(v => (
													<MenuItem key={v.voice_id} value={v.voice_id}>
														{v.voice_id}
													</MenuItem>
												))}
											</Select>
										</FormControl>

										<FormControl sx={{minWidth: 200}} size="small">
											<InputLabel>Category</InputLabel>
											<Select
												label="Category"
												value={videoCategoryId}
												onChange={(e) => setVideoCategoryId(e.target.value)}
											>
												{jobCategories.map(c => (
													<MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
												))}
											</Select>
										</FormControl>
									</Box>
								}
								right={
									<Box>
										<FormControlLabel
											control={<Switch color="secondary" checked={hideExcluded}
											                 onChange={(e) => setHideExcluded(e.target.checked)}/>}
											label="Selected Only"
											sx={{whiteSpace: 'nowrap', ml: 2, mr: 0}}
										/>
									</Box>
								}
							/>
						</Box>


						{videoProducts.length === 0 && (
							<Box textAlign="center" mt={10}>
								<Button
									onClick={() => setShowProductsDrawer(true)}
									variant="contained"
									color="primary"
									size="large"
									sx={{
										py: 2,
										px: 4,
										backgroundColor: theme.palette.primary.main,
										color: theme.palette.primary.contrastText
									}}
								>
									<Add/> Add a Product
								</Button>
							</Box>
						)}

						{videoProducts.map(vp => (
							<ProductContentSelector
								key={`vp-${vp.productId}`}
								videoProduct={vp}
								hideExcluded={hideExcluded}
								onChange={handleVideoProductChange}
								onGetCustomClip={handleGetCustomClip}
								onRemove={() => handleRemoveVideoProduct(vp.productId)}
							/>
						))}
					</Grid>

					<Drawer anchor="right" open={!!drawerOpen} variant="persistent">
						<Box sx={{width: '400px'}}>
							<Box display="flex" flexDirection="row" justifyContent="space-between">
								<Box sx={{pl: 2}}>
									<h3>{drawerTitle}</h3>
								</Box>

								<IconButton
									onClick={() => {
										if (showCustomClipsDrawer) {
											if (resolveSelectClip) resolveSelectClip(null)
											setShowCustomClipsDrawer(false)
											setResolveSelectClip(null)
										}
										else if (showProductsDrawer) {
											setShowProductsDrawer(false)
										}
									}}
								>
									<Close color="error" size="20"/>
								</IconButton>
							</Box>

							<Box sx={{px: 2, pb: 2}}>
								{showProductsDrawer && (
									<>
										<FormControl fullWidth size="small" sx={{mb: 1}}>
											<InputLabel id="drawer-category-label">Category</InputLabel>
											<Select
												labelId="drawer-category-label"
												value={category}
												label="Category"
												onChange={(e) => setCategory(e.target.value)}
											>
												<MenuItem value="All">All</MenuItem>
												{categories.map(c => (
													<MenuItem key={`cat-${c.id}`} value={c.id}>{c.name}</MenuItem>
												))}
											</Select>
										</FormControl>

										<FormControl fullWidth size="small">
											<input
												type="text"
												placeholder="Filter..."
												value={keyword}
												onChange={(e) => setKeyword(e.target.value)}
												style={{
													fontSize: 16,
													width: '100%',
													padding: '10px',
													paddingLeft: '12px',
													borderRadius: '4px',
													border: '1px solid #ccc'
												}}
											/>
										</FormControl>
									</>
								)}
							</Box>

							{drawerBody}
						</Box>
					</Drawer>
				</Grid>
			</DialogContent>

			{
				showScript &&
				<Box
					position="fixed"
					top={0}
					right={0}
					left={0}
					bottom={0}
					zIndex={2000}
					backgroundColor="#ffffff"
					display="flex"
					flexDirection="column"
				>
					{/* Header */}
					<Box px={2} py={2} backgroundColor="#f3f3f3">
						<LeftRight
							left={<h2 style={{margin: 0}}>Video Script Preview</h2>}
							right={
								<IconButton
									size="large"
									onClick={() => setShowScript(false)}
								>
									<Close fontSize="20px" color="error" />
								</IconButton>
							}
						/>
					</Box>

					<Divider />

					{/* Scrollable content */}
					<Box
						flex={1}
						overflow="auto"
						px={2}
						py={1}
					>
						<pre style={{margin: 0}}>
							{JSON.stringify(videoScript, null, 2)}
						</pre>
					</Box>
				</Box>

			}


		</Dialog>
	)
}

export default VideoJobDialog
